import { ResumeSensitiveDialog } from '@seek/profile-base-ui';
import { useTranslations } from '@vocab/react';
import {
  Box,
  ButtonIcon,
  Column,
  Columns,
  Dropdown,
  IconDelete,
  IconResume,
  TextLinkButton,
} from 'braid-design-system';
import { useState } from 'react';

import type { Resume } from 'src/__generated__/graphql';
import { useConfig } from 'src/context/config';
import { Page, PageSection, useTrackingClient } from 'src/context/tracking';
import { useDialogRouterState } from 'src/modules/use-dialog-router-state';
import { formatDateShort } from 'src/modules/utils';
import { DocumentDeleteDialog } from 'src/pages/Documents/components/DocumentDeleteDialog/DocumentDeleteDialog';
import { DocumentDownloadButton } from 'src/pages/Documents/components/DocumentDownloadButton/DocumentDownloadButton';

import translations from './.vocab';

interface DocumentSelectProps {
  id: string;
  loading?: boolean;
  defaultValue?: File | string | null;
  value: string;
  documentList?: Resume[];
  onChange: (value: string) => void;
  valid: boolean;
  message: string;
}

const DONT_INCLUDE_RESUME_ID = 'dont-include';

const DocumentSelect = ({
  id,
  loading = false,
  defaultValue,
  value,
  documentList = [],
  onChange,
  valid,
  message,
}: DocumentSelectProps) => {
  const { t } = useTranslations(translations);
  const client = useTrackingClient();
  const {
    isOpen: deleteDocumentDialogOpen,
    open: openDeleteDocumentDialog,
    close: closeDeleteDocumentDialog,
  } = useDialogRouterState(`delete-document-${id}`);
  const [showDialog, setShowDialog] = useState(false);
  const { config } = useConfig();

  const hasResumeSelected = value && value !== DONT_INCLUDE_RESUME_ID;
  const hasSpi = documentList.find((doc) => doc.id === value)?.fileMetadata
    .sensitiveDataInfo?.isDetected;

  const handleSpiClick = () => {
    client.trackLink({
      link: 'sensitive_information_dialog_displayed',
      eventName: 'sensitive_information_dialog_displayed',
      currentPage: Page.DOCUMENT_PAGE,
      currentPageSection: PageSection.DOCUMENTS,
    });

    setShowDialog(true);
  };

  const getTone = () => {
    if (!valid) {
      return 'critical';
    }
    if (hasSpi) {
      return 'caution';
    }
    return 'positive';
  };

  const getMessage = () => {
    if (valid && hasResumeSelected) {
      return hasSpi ? (
        <Box component="span" role="status">
          {t('Spi', {
            TextLinkButton: (v) => (
              <TextLinkButton onClick={handleSpiClick} weight="weak">
                {v}
              </TextLinkButton>
            ),
          })}
        </Box>
      ) : (
        <Box component="span" role="status">
          {t('Resume attached')}
        </Box>
      );
    }
    return message;
  };

  const handleSpiLinkClick = () => {
    client.trackLink({
      link: 'sensitive_information_dialog_pressed',
      eventName: 'sensitive_information_dialog_pressed',
      currentPage: Page.DOCUMENT_PAGE,
      currentPageSection: PageSection.DOCUMENTS,
      linkText: 'sensitive Information: what you need to know',
    });
  };

  const selections = documentList.map((doc) => (
    <option key={doc.id} value={doc.id}>
      {`${formatDateShort(doc.createdDateUtc)} - ${doc.fileMetadata?.name}`}
    </option>
  ));

  const handleChange = (e: React.FormEvent<HTMLSelectElement>) => {
    const currentResumeId = e.currentTarget.value;
    const selection =
      currentResumeId === DONT_INCLUDE_RESUME_ID
        ? currentResumeId
        : 'not-preselected';
    const resumeSelectedType =
      currentResumeId === defaultValue ? 'preselected' : selection;

    client.trackLink({
      link: 'resume_dropdown_pressed',
      eventName: 'resume_dropdown_pressed',
      currentPage: Page.DOCUMENT_PAGE,
      currentPageSection: PageSection.RESUME,
      resumeSelectedType,
    });
    onChange(e.currentTarget.value);
  };

  const clearSelection = () => onChange('');

  return (
    <>
      <Columns space="small" alignY="center">
        <Column>
          <Dropdown
            data={{ testid: 'select-input' }}
            id={id}
            label=""
            placeholder={t('Please select a resume')}
            value={value || ''}
            disabled={loading}
            icon={<IconResume />}
            onChange={handleChange}
            message={getMessage()}
            tone={getTone()}
          >
            {selections}
          </Dropdown>
        </Column>
        {hasResumeSelected ? (
          <Column width="content">
            <Box paddingBottom="medium">
              <Columns space="small">
                <Column>
                  <ButtonIcon
                    id="deleteResume"
                    label={t('Delete')}
                    icon={<IconDelete />}
                    onClick={openDeleteDocumentDialog}
                    data={{ testid: 'deleteButton' }}
                  />
                </Column>
                <Column width="content">
                  <DocumentDownloadButton
                    id="downloadResume"
                    resumeId={value}
                  />
                </Column>
              </Columns>
            </Box>
          </Column>
        ) : null}
        <DocumentDeleteDialog
          resumeId={value}
          open={Boolean(value && deleteDocumentDialogOpen)}
          onClose={closeDeleteDocumentDialog}
          onSuccess={() => {
            clearSelection();
            closeDeleteDocumentDialog();
          }}
        />
      </Columns>
      <ResumeSensitiveDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        onTrackSensitiveInfoLink={() => handleSpiLinkClick()}
        zone={config.ZONE}
        language={config.LANGUAGE}
      />
    </>
  );
};

export { DocumentSelect, DONT_INCLUDE_RESUME_ID };
